import { isEmptyObject } from '../../../tools/utils/objectUtils';
import { getCurrentSite, areCookiesAuthorized, getCookies } from '../../../browser/cookie';
import { SESSION_COOKIE_EXPIRATION_DELAY, SESSION_TIME_OUT_DELAY, SessionPersistence } from '../sessionConstants';
import { toSessionString, toSessionState } from '../sessionState';
import { Observable } from '../../../tools/observable';
import { mockable } from '../../../tools/mockable';
import { monitorError } from '../../../tools/monitor';
import { createCookieAccess } from '../../../browser/cookieAccess';
import { SESSION_STORE_KEY, LEGACY_SESSION_STORE_KEY } from './sessionStoreStrategy';
const SESSION_COOKIE_VERSION = 0;
export function selectCookieStrategy(initConfiguration) {
    const cookieOptions = buildCookieOptions(initConfiguration);
    return cookieOptions && areCookiesAuthorized(cookieOptions)
        ? { type: SessionPersistence.COOKIE, cookieOptions }
        : undefined;
}
// Promise chain serializes calls when Web Locks are unavailable
let pendingChain;
export function initCookieStrategy(cookieOptions, configuration) {
    const sessionObservable = new Observable();
    const trackAnonymousUser = !!configuration.trackAnonymousUser;
    const opts = encodeCookieOptions(cookieOptions);
    const cookieAccess = mockable(createCookieAccess)(SESSION_STORE_KEY, configuration, cookieOptions);
    let isFirstCall = true;
    cookieAccess.observable.subscribe(() => {
        cookieAccess
            .getAll()
            .then((cookieValues) => {
            sessionObservable.notify({ cookieValues, sessionState: findMatchingSessionState(cookieValues, opts) });
        })
            .catch(monitorError);
    });
    function applyAndWrite(fn) {
        return cookieAccess.getAllAndSet((cookieValues) => {
            let currentState = findMatchingSessionState(cookieValues, opts);
            if (isFirstCall && isEmptyObject(currentState)) {
                currentState = findMatchingSessionState(getCookies(LEGACY_SESSION_STORE_KEY), opts);
            }
            isFirstCall = false;
            const newState = fn(currentState);
            const sessionString = buildSessionString(newState, cookieOptions);
            const expireDelay = trackAnonymousUser ? SESSION_COOKIE_EXPIRATION_DELAY : SESSION_TIME_OUT_DELAY;
            return { value: sessionString, expireDelay };
        });
    }
    return {
        async setSessionState(fn) {
            if (typeof navigator !== 'undefined' && navigator.locks) {
                await navigator.locks.request(SESSION_STORE_KEY, () => applyAndWrite(fn));
            }
            else {
                pendingChain = (pendingChain ?? Promise.resolve()).then(() => applyAndWrite(fn));
                await pendingChain;
            }
        },
        sessionObservable,
    };
}
function findMatchingSessionState(items, opts) {
    let sessionState;
    // reverse the cookies so that if there is no match, the cookie returned is the first one
    for (const item of items.slice().reverse()) {
        sessionState = toSessionState(item);
        if (sessionState.c === opts) {
            break;
        }
    }
    // remove the cookie options from the session state as this is not part of the session state
    delete sessionState?.c;
    return sessionState ?? {};
}
function buildSessionString(sessionState, cookieOptions) {
    return toSessionString(isEmptyObject(sessionState) ? sessionState : { ...sessionState, c: encodeCookieOptions(cookieOptions) });
}
export function buildCookieOptions(initConfiguration) {
    const cookieOptions = {};
    cookieOptions.secure =
        !!initConfiguration.useSecureSessionCookie || !!initConfiguration.usePartitionedCrossSiteSessionCookie;
    cookieOptions.crossSite = !!initConfiguration.usePartitionedCrossSiteSessionCookie;
    cookieOptions.partitioned = !!initConfiguration.usePartitionedCrossSiteSessionCookie;
    if (initConfiguration.trackSessionAcrossSubdomains) {
        const currentSite = getCurrentSite();
        if (!currentSite) {
            return;
        }
        cookieOptions.domain = currentSite;
    }
    return cookieOptions;
}
function encodeCookieOptions(cookieOptions) {
    const domainCount = cookieOptions.domain ? cookieOptions.domain.split('.').length - 1 : 0;
    /* eslint-disable no-bitwise */
    let byte = 0;
    byte |= SESSION_COOKIE_VERSION << 5; // Store version in upper 3 bits
    byte |= domainCount << 1; // Store domain count in next 4 bits
    byte |= cookieOptions.crossSite ? 1 : 0; // Store useCrossSiteScripting in next bit
    /* eslint-enable no-bitwise */
    return byte.toString(16); // Convert to hex string
}
//# sourceMappingURL=sessionInCookie.js.map